import React, { useState, useEffect } from 'react';
import { obtenerGeocercas, crearGeocerca, eliminarGeocerca, Geocerca, CrearGeocercaRequest } from '../../apis/geocercasApi';
import { Plus, Trash2, Edit, MapPin, Circle } from 'lucide-react';

interface GestionGeocercasProps {
  token: string;
  onGeocercaCreated?: () => void;
}

const GestionGeocercas: React.FC<GestionGeocercasProps> = ({ token, onGeocercaCreated }) => {
  const [geocercas, setGeocercas] = useState<Geocerca[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<CrearGeocercaRequest>({
    nombre: '',
    descripcion: '',
    tipo: 'circular',
    centro_latitud: 19.4326,
    centro_longitud: -99.1332,
    radio: 500,
    color: '#3388ff',
    accion_entrada: 'notificar',
    accion_salida: 'notificar',
  });

  const cargarGeocercas = async () => {
    try {
      setLoading(true);
      const data = await obtenerGeocercas(token);
      setGeocercas(data);
    } catch (error) {
      console.error('Error al cargar geocercas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarGeocercas();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await crearGeocerca(formData, token);
      setShowModal(false);
      setFormData({
        nombre: '',
        descripcion: '',
        tipo: 'circular',
        centro_latitud: 19.4326,
        centro_longitud: -99.1332,
        radio: 500,
        color: '#3388ff',
        accion_entrada: 'notificar',
        accion_salida: 'notificar',
      });
      await cargarGeocercas();
      if (onGeocercaCreated) onGeocercaCreated();
    } catch (error) {
      console.error('Error al crear geocerca:', error);
    }
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta geocerca?')) {
      try {
        await eliminarGeocerca(id, token);
        await cargarGeocercas();
        if (onGeocercaCreated) onGeocercaCreated();
      } catch (error) {
        console.error('Error al eliminar geocerca:', error);
      }
    }
  };

  if (loading) {
    return <div className="p-4">Cargando geocercas...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Gestión de Geocercas</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={20} />
          Nueva Geocerca
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {geocercas.map((geocerca) => (
          <div key={geocerca.id} className="bg-white rounded-lg shadow-md p-4 border">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                {geocerca.tipo === 'circular' ? (
                  <Circle size={20} style={{ color: geocerca.color }} />
                ) : (
                  <MapPin size={20} style={{ color: geocerca.color }} />
                )}
                <h3 className="font-semibold text-lg">{geocerca.nombre}</h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEliminar(geocerca.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mb-2">{geocerca.descripcion}</p>
            
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Tipo:</span>
                <span className="capitalize">{geocerca.tipo}</span>
              </div>
              {geocerca.tipo === 'circular' && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Radio:</span>
                  <span>{geocerca.radio}m</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Entrada:</span>
                <span className="capitalize">{geocerca.accion_entrada}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Salida:</span>
                <span className="capitalize">{geocerca.accion_salida}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Estado:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  geocerca.activa ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {geocerca.activa ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal para crear geocerca */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nueva Geocerca</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'circular' | 'poligonal' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="circular">Circular</option>
                  <option value="poligonal">Poligonal</option>
                </select>
              </div>

              {formData.tipo === 'circular' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Latitud
                      </label>
                      <input
                        type="number"
                        step="any"
                        required
                        value={formData.centro_latitud}
                        onChange={(e) => setFormData({ ...formData, centro_latitud: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Longitud
                      </label>
                      <input
                        type="number"
                        step="any"
                        required
                        value={formData.centro_longitud}
                        onChange={(e) => setFormData({ ...formData, centro_longitud: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Radio (metros)
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.radio}
                      onChange={(e) => setFormData({ ...formData, radio: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acción Entrada
                  </label>
                  <select
                    value={formData.accion_entrada}
                    onChange={(e) => setFormData({ ...formData, accion_entrada: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="notificar">Notificar</option>
                    <option value="bloquear">Bloquear</option>
                    <option value="registrar">Registrar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acción Salida
                  </label>
                  <select
                    value={formData.accion_salida}
                    onChange={(e) => setFormData({ ...formData, accion_salida: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="notificar">Notificar</option>
                    <option value="bloquear">Bloquear</option>
                    <option value="registrar">Registrar</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Crear Geocerca
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionGeocercas;
